'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/ui-kit/utils';
import { SEARCH_CATEGORIES, SECTION_LIMIT, TAB_LIMIT } from './core/constants';
import { rememberQuery } from './core/recentStore';
import { useSearch } from './core/SearchContext';
import type { SearchCategory, SearchTab } from './core/types';
import type { SearchResults } from './hooks/useSearchResults';
import { NewsRow } from './rows/NewsRow';
import { PairRow } from './rows/PairRow';
import { PredictionRow } from './rows/PredictionRow';
import { TraderRow } from './rows/TraderRow';
import { NoResults } from './shared/NoResults';
import { SearchSkeleton } from './shared/SearchSkeleton';
import { Section, SectionAction } from './shared/Section';

interface CategoryRowsProps {
  category: SearchCategory;
  results: SearchResults;
  limit: number;
}

/** A category's rows, the first `limit` of them. */
function CategoryRows({ category, results, limit }: CategoryRowsProps) {
  switch (category) {
    case 'crypto':
      return results.pairs.slice(0, limit).map((pair) => <PairRow key={pair.id} pair={pair} />);
    case 'predictions':
      return results.predictions
        .slice(0, limit)
        .map((prediction) => <PredictionRow key={prediction.id} prediction={prediction} />);
    case 'traders':
      return results.traders
        .slice(0, limit)
        .map((trader) => <TraderRow key={trader.userId} trader={trader} />);
    case 'news':
      return results.news.slice(0, limit).map((item) => <NewsRow key={item.id} item={item} />);
  }
}

interface CategorySectionProps {
  category: SearchCategory;
  results: SearchResults;
  limit: number;
  /** Under All with others beside it: the way to its own tab. */
  onSeeAll?: (category: SearchCategory) => void;
}

/** One category's answer: its caption with the count, then its rows. */
function CategorySection({ category, results, limit, onSeeAll }: CategorySectionProps) {
  const t = useTranslations('search');
  const handleSeeAll = () => onSeeAll?.(category);
  return (
    <Section
      title={t(`tabs.${category}`)}
      count={results.counts[category]}
      action={onSeeAll ? <SectionAction label={t('seeAll')} onClick={handleSeeAll} /> : undefined}
    >
      <CategoryRows category={category} results={results} limit={limit} />
    </Section>
  );
}

interface ResultsViewProps {
  results: SearchResults;
  tab: SearchTab;
  onTabChange: (tab: SearchTab) => void;
}

/**
 * The search with a query (Figma "Typing"): under All a section per category
 * that found something, or — when only one did — its full list; on a
 * category's tab, that category alone. Earlier results stay up, dimmed, while
 * a newer query loads.
 */
export function ResultsView({ results, tab, onTabChange }: ResultsViewProps) {
  const { query } = useSearch();

  if (results.loading) return <SearchSkeleton />;

  const found = SEARCH_CATEGORIES.filter((category) => results.counts[category] > 0);

  // "See all" is as good as Enter: the query earned its chip.
  const handleSeeAll = (category: SearchCategory) => {
    rememberQuery(query);
    onTabChange(category);
  };

  // A tab that found nothing never gets here (it steps back to All), so empty means empty everywhere.
  if (found.length === 0) return <NoResults query={query} />;

  const categories = tab === 'all' ? found : [tab];
  const single = categories.length === 1;

  return (
    <div
      className={cn(
        'flex flex-1 flex-col gap-1.5 transition-opacity duration-150',
        results.refreshing && 'opacity-60',
      )}
    >
      {categories.map((category) => (
        <CategorySection
          key={category}
          category={category}
          results={results}
          limit={single ? TAB_LIMIT : SECTION_LIMIT}
          onSeeAll={single ? undefined : handleSeeAll}
        />
      ))}
    </div>
  );
}
